import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Spin,
  Select,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import api from '../services/api';
import { authService } from '../services/auth';
import { formatCurrency } from '../utils/currency';
import dayjs from 'dayjs';
import { formatProductDimensions } from '../utils/productDimensions';
import { formatLocalizedPercent } from '../utils/numberFormatting';
import { formatWarehouseQuantity } from '../modules/warehouse/warehouseQuantityConversion';
import { InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ReportSection } from '../modules/reports/ReportTab';
import { ObjectCardEmpty } from '../components/layout/ObjectCard';
import './BatchProfitability.css';

// ─── DTOs ────────────────────────────────────────────────────────────────────

interface BatchRow {
  id: string;
  batchNumber: string;
  productVariantId: string;
  variantSku: string;
  variantName: string;
  /** Display only: batch dimensions (PZ line, catalog fallback) and category profile. */
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  productCategoryDimensionDisplayProfile?: string | null;
  pzDocumentId: string | null;
  pzNumber: string | null;
  purchaseDate: string;
  quantityUnit: string | null;
  bought: number;
  boughtPcs?: number | null;
  boughtM2?: number | null;
  boughtM3?: number | null;
  inStock: number;
  inStockPcs?: number | null;
  inStockM2?: number | null;
  inStockM3?: number | null;
  soldQty: number;
  soldQtyPcs?: number | null;
  soldQtyM2?: number | null;
  soldQtyM3?: number | null;
  unitCostPLN: number;
  totalCostPLN: number;
  cogsRealizedPLN: number;
  stockValuePLN: number;
  saleRevenuePLN: number | null;
  batchMarginPLN: number | null;
  marginPercent: number | null;
  status: 'IN_PROGRESS' | 'CLOSED';
}

interface ConsumedOrderRow {
  orderId: string;
  orderNumber: string;
  quantityUnit: string | null;
  qtyFromBatch: number;
  cogsFromBatchPLN: number;
  saleRevenueFromBatchPLN: number | null;
  marginFromBatchPLN: number | null;
  perUnitMarginPLN: number | null;
  orderRevenuePLN: number;
  orderTotalCostPLN: number;
  orderMarginPLN: number;
}

// ─── API ─────────────────────────────────────────────────────────────────────

async function fetchBatchProfitability(statusFilter?: string): Promise<BatchRow[]> {
  const params: Record<string, string> = {};
  if (statusFilter) params.status = statusFilter;
  const response = await api.get('/reports/batch-profitability', { params });
  return response.data;
}

async function fetchBatchConsumingOrders(batchId: string): Promise<ConsumedOrderRow[]> {
  const response = await api.get(`/reports/batch-profitability/${batchId}`);
  return response.data;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Sign carries meaning on margin: profit in success ink, loss in danger ink.
function marginClass(value: number): string {
  if (value > 0) return 'report-num report-positive';
  if (value < 0) return 'report-num report-negative';
  return 'report-num';
}

function renderPln(value: number | null | undefined): React.ReactNode {
  if (value === null || value === undefined) return <span className="report-muted">—</span>;
  return <span className={marginClass(value)}>{formatCurrency(value, 'PLN')}</span>;
}

function renderPercent(value: number | null | undefined, language?: string): React.ReactNode {
  if (value === null || value === undefined) return <span className="report-muted">—</span>;
  return <span className={marginClass(value)}>{formatLocalizedPercent(value, language)}</span>;
}

// ─── Component ───────────────────────────────────────────────────────────────

const BatchProfitability: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['inventory', 'common']);
  const feedback = useFeedback();

  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  // Expanded rows: batchId → orders
  const [expandedOrders, setExpandedOrders] = useState<Record<string, ConsumedOrderRow[]>>({});
  const [loadingOrders, setLoadingOrders] = useState<Record<string, boolean>>({});

  // Redirect if no margin visibility — defensive guard in addition to nav gating
  useEffect(() => {
    if (!authService.canViewFinancialReports()) {
      navigate('/app/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function load() {
    setLoading(true);
    try {
      const data = await fetchBatchProfitability(statusFilter);
      setBatches(data);
    } catch (err: any) {
      feedback.error(getApiErrorMessage(err, t, 'inventory:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }

  async function handleExpand(expanded: boolean, record: BatchRow) {
    if (!expanded) return;
    if (expandedOrders[record.id]) return; // already loaded
    setLoadingOrders(prev => ({ ...prev, [record.id]: true }));
    try {
      const orders = await fetchBatchConsumingOrders(record.id);
      setExpandedOrders(prev => ({ ...prev, [record.id]: orders }));
    } catch (err: any) {
      feedback.error(getApiErrorMessage(err, t, 'inventory:error.fetchFailed'));
    } finally {
      setLoadingOrders(prev => ({ ...prev, [record.id]: false }));
    }
  }

  // ── Sub-table: consuming orders ──────────────────────────────────────────
  const orderColumns = [
    {
      title: t('inventory:batchProfitability.orderNumber'),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (text: string, record: ConsumedOrderRow) => (
        <Button type="link" size="small" onClick={() => navigate(`/app/orders/${record.orderId}`)}>
          {text}
        </Button>
      ),
    },
    {
      title: t('inventory:batchProfitability.qtyFromBatch'),
      dataIndex: 'qtyFromBatch',
      key: 'qtyFromBatch',
      align: 'right' as const,
      render: (value: number, record: ConsumedOrderRow) => (
        record.quantityUnit
          ? formatWarehouseQuantity(value, record.quantityUnit, i18n.language)
          : value
      ),
    },
    {
      title: t('inventory:batchProfitability.cogsFromBatch'),
      dataIndex: 'cogsFromBatchPLN',
      key: 'cogsFromBatchPLN',
      align: 'right' as const,
      render: (v: number) => <span className="report-num">{formatCurrency(v, 'PLN')}</span>,
    },
    {
      title: t('inventory:batchProfitability.saleFromBatch'),
      dataIndex: 'saleRevenueFromBatchPLN',
      key: 'saleRevenueFromBatchPLN',
      align: 'right' as const,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.marginFromBatch'),
      dataIndex: 'marginFromBatchPLN',
      key: 'marginFromBatchPLN',
      align: 'right' as const,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.perUnitMargin'),
      dataIndex: 'perUnitMarginPLN',
      key: 'perUnitMarginPLN',
      align: 'right' as const,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.orderMargin'),
      dataIndex: 'orderMarginPLN',
      key: 'orderMarginPLN',
      align: 'right' as const,
      render: renderPln,
    },
  ];

  // ── Main table: batches ──────────────────────────────────────────────────
  const batchColumns = [
    {
      // Batch and product share the one text column (two lines, ellipsis);
      // every number gets its own narrow column and is never cut.
      title: t('inventory:batchProfitability.batchNumber'),
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      render: (_: string, record: BatchRow) => {
        const dimensions = formatProductDimensions(record);
        const status = t(`inventory:batchProfitability.${record.status === 'CLOSED' ? 'statusClosed' : 'statusInProgress'}`);
        const date = record.purchaseDate ? dayjs(record.purchaseDate).format('DD.MM.YYYY') : '—';
        const product = [record.variantSku, record.variantName, dimensions].filter(Boolean).join(' · ');
        return (
          <div className="batch-profit__batch">
            <span className="batch-profit__head report-cut" title={`${record.pzNumber || record.batchNumber} · ${date} · ${status}`}>
              <span className="batch-profit__name report-num">{record.pzNumber || record.batchNumber}</span>
              {' · '}
              <span className="report-num">{date}</span>
              {' · '}
              <span className={record.status === 'CLOSED' ? 'batch-profit__closed' : 'batch-profit__open'}>{status}</span>
            </span>
            <span
              className="batch-profit__meta report-cut"
              title={product}
              data-testid={dimensions ? `batch-profitability-dimensions-${record.id}` : undefined}
            >
              {product}
            </span>
          </div>
        );
      },
    },
    {
      title: t('inventory:batchProfitability.bought'),
      dataIndex: 'bought',
      key: 'bought',
      width: 96,
      align: 'right' as const,
      render: (value: number, record: BatchRow) => (
        <span className="report-num">
          {record.quantityUnit ? formatWarehouseQuantity(value, record.quantityUnit, i18n.language) : value}
        </span>
      ),
    },
    {
      title: t('inventory:batchProfitability.sold'),
      dataIndex: 'soldQty',
      key: 'soldQty',
      width: 96,
      align: 'right' as const,
      render: (value: number, record: BatchRow) => (
        <span className="report-num">
          {record.quantityUnit ? formatWarehouseQuantity(value, record.quantityUnit, i18n.language) : value}
        </span>
      ),
    },
    {
      title: t('inventory:batchProfitability.inStock'),
      dataIndex: 'inStock',
      key: 'inStock',
      width: 96,
      align: 'right' as const,
      render: (value: number, record: BatchRow) => (
        <span className="report-num">
          {record.quantityUnit ? formatWarehouseQuantity(value, record.quantityUnit, i18n.language) : value}
        </span>
      ),
    },
    {
      title: t('inventory:batchProfitability.cogs'),
      dataIndex: 'cogsRealizedPLN',
      key: 'cogsRealizedPLN',
      width: 120,
      align: 'right' as const,
      render: (v: number) => <span className="report-num">{formatCurrency(v, 'PLN')}</span>,
    },
    {
      title: t('inventory:batchProfitability.saleRevenue'),
      dataIndex: 'saleRevenuePLN',
      key: 'saleRevenuePLN',
      width: 120,
      align: 'right' as const,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.batchMargin'),
      dataIndex: 'batchMarginPLN',
      key: 'batchMarginPLN',
      width: 120,
      align: 'right' as const,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.marginPercent'),
      dataIndex: 'marginPercent',
      key: 'marginPercent',
      width: 80,
      align: 'right' as const,
      render: (value: number | null | undefined) => renderPercent(value, i18n.language),
    },
  ];

  const expandedRowRender = (record: BatchRow) => {
    if (loadingOrders[record.id]) {
      return <div className="batch-profit__loading"><Spin size="small" /></div>;
    }
    const orders = expandedOrders[record.id] || [];
    return (
      <Table
        className="report-table batch-profit__orders"
        dataSource={orders}
        columns={orderColumns}
        rowKey="orderId"
        size="small"
        pagination={false}
        locale={{ emptyText: t('inventory:batchProfitability.noOrders') }}
      />
    );
  };

  return (
    <div className="report-tab batch-profit">
      <div className="report-controls">
        <Select
          allowClear
          placeholder={t('inventory:batchProfitability.filterStatus')}
          className="batch-profit__status"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v)}
          options={[
            { value: 'IN_PROGRESS', label: t('inventory:batchProfitability.statusInProgress') },
            { value: 'CLOSED', label: t('inventory:batchProfitability.statusClosed') },
          ]}
        />
        <div className="report-controls__end">
          <span className="report-controls__counter">
            {t('inventory:batchProfitability.counter', { count: batches.length })}
          </span>
        </div>
      </div>

      <ReportSection
        icon={<InboxOutlined />}
        tone="teal"
        title={t('inventory:batchProfitability.sectionTitle')}
        count={batches.length}
        lead={t('inventory:batchProfitability.lead')}
        actions={(
          <Tooltip title={t('inventory:batchProfitability.attributionNote')} placement="topRight">
            <InfoCircleOutlined className="batch-profit__info" aria-label={t('inventory:batchProfitability.attributionNote')} />
          </Tooltip>
        )}
      >
        <Table
          className="report-table"
          dataSource={batches}
          columns={batchColumns}
          rowKey="id"
          size="small"
          loading={loading}
          tableLayout="fixed"
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            defaultPageSize: 25,
            hideOnSinglePage: true,
          }}
          expandable={{
            expandedRowRender,
            onExpand: handleExpand,
            // Invalidate stale cached orders when row collapses so a
            // re-expand always fetches fresh data
            onExpandedRowsChange: (expandedKeys) => {
              setExpandedOrders(prev => {
                const next: typeof prev = {};
                expandedKeys.forEach(k => {
                  if (prev[k as string]) next[k as string] = prev[k as string];
                });
                return next;
              });
            },
          }}
          locale={{
            emptyText: (
              <ObjectCardEmpty
                icon={<InboxOutlined />}
                text={t('inventory:batchProfitability.noBatchesHint')}
                action={(
                  <Button onClick={() => navigate('/app/pz')}>
                    {t('inventory:batchProfitability.openReceipts')}
                  </Button>
                )}
              />
            ),
          }}
        />
      </ReportSection>
    </div>
  );
};

export default BatchProfitability;
